import { Link } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';

type Dimension = {
  num: string;
  title: string;
  weight: string;
  body: string;
};

const DIMENSIONS: Dimension[] = [
  {
    num: '01',
    title: 'llms.txt presence & quality',
    weight: '8%',
    body: 'Does your domain serve a discoverable llms.txt? Are the routes, examples, and capability summary fresh and parseable by a crawling agent?',
  },
  {
    num: '02',
    title: 'OpenAPI completeness',
    weight: '14%',
    body: 'Every operation has descriptions, examples, and accurate response schemas. No undocumented endpoints, no anyOf/oneOf without discriminators.',
  },
  {
    num: '03',
    title: 'Auth & error semantics',
    weight: '10%',
    body: 'Auth scopes are documented per endpoint, error responses use a consistent envelope, and rate-limit headers are advertised.',
  },
  {
    num: '04',
    title: 'SDK consistency',
    weight: '12%',
    body: 'Your published SDKs match the spec — no drift between method signatures, types, and what an agent sees in the OpenAPI document.',
  },
  {
    num: '05',
    title: 'Time-to-first-call',
    weight: '12%',
    body: 'A real coding agent (Claude, Cursor, Copilot) is given your docs and asked to make an authenticated call. We measure how long it takes and how many turns.',
  },
  {
    num: '06',
    title: 'Code samples & cookbook coverage',
    weight: '8%',
    body: 'Multi-language samples, copyable cURL, and a recipes section that solves the top integration tasks (auth, pagination, webhooks, retries).',
  },
  {
    num: '07',
    title: 'MCP server presence',
    weight: '8%',
    body: 'A discoverable Model Context Protocol server is available so agents can call your API as native tools, not synthesized HTTP requests.',
  },
  {
    num: '08',
    title: 'Cursor / Copilot rules',
    weight: '8%',
    body: 'IDE rule files (.cursorrules, .github/copilot-instructions.md) are published and reflect current SDK shape and your style guide.',
  },
  {
    num: '09',
    title: 'Changelog discipline',
    weight: '10%',
    body: 'Versioned, machine-readable changelog. Breaking changes flagged. Deprecation windows clear. Agents can reason about what changed.',
  },
  {
    num: '10',
    title: 'Stability & observability',
    weight: '10%',
    body: 'Public status page, idempotency keys on writes, request IDs in error responses, and traceable rate-limit feedback for retry logic.',
  },
];

export function MethodologyPage() {
  return (
    <div className="mx-auto max-w-6xl px-6 py-20">
      <section className="max-w-3xl">
        <div className="mb-5 inline-block rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
          Methodology
        </div>
        <h1 className="text-4xl font-semibold tracking-tight md:text-5xl">
          How we score agent-readiness.
        </h1>
        <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
          Athena scores your public API surface across 10 dimensions on a 0–100 scale. Every
          dimension is independently weighted, deterministic where possible, and grounded in what
          AI coding agents actually need to integrate successfully.
        </p>
      </section>

      <section className="mt-12 grid gap-4 md:grid-cols-3">
        <Stat label="Dimensions" value="10" />
        <Stat label="Sub-checks per audit" value="120+" />
        <Stat label="Rerunnable" value="On every PR" />
      </section>

      <section className="mt-16">
        <h2 className="text-2xl font-semibold tracking-tight">The 10 dimensions</h2>
        <p className="mt-2 max-w-2xl text-slate-600 dark:text-slate-400">
          Weights reflect the relative impact each dimension has on agent integration success.
          Together they sum to 100.
        </p>
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {DIMENSIONS.map((d) => (
            <DimensionCard key={d.num} d={d} />
          ))}
        </div>
      </section>

      <section className="mt-20">
        <h2 className="text-2xl font-semibold tracking-tight">How a score is produced</h2>
        <ol className="mt-6 space-y-6">
          <Step
            n="1"
            title="Collect"
            body="We fetch your OpenAPI spec, docs URL, and (optionally) SDK repos. No source code is stored — we hash the inputs to detect drift."
          />
          <Step
            n="2"
            title="Run deterministic checks"
            body="Static checks for llms.txt validity, OpenAPI completeness, error envelope consistency, SDK-vs-spec drift, and changelog discipline."
          />
          <Step
            n="3"
            title="Run agent-driven checks"
            body="A real LLM agent attempts the canonical first-call task using only your published surface. We log turns, errors, and time-to-success."
          />
          <Step
            n="4"
            title="Weight and aggregate"
            body="Each dimension produces a 0–100 sub-score. Sub-scores are weighted (per the table above) and aggregated into the headline score."
          />
          <Step
            n="5"
            title="Prioritize fixes"
            body="Findings are ranked by score impact × ease-to-fix. The report leads with the five changes that move your score the most."
          />
        </ol>
      </section>

      <section className="mt-20 rounded-xl border border-slate-200 bg-slate-50 p-8 text-center dark:border-slate-800 dark:bg-slate-950">
        <h2 className="text-2xl font-semibold tracking-tight">See it on a real API.</h2>
        <p className="mx-auto mt-2 max-w-xl text-slate-600 dark:text-slate-400">
          The sample report walks through every dimension on a public API, shows the prioritized
          fixes, and what the score looks like after each one ships.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <Button size="lg" asChild>
            <Link to="/sample-report">See a sample report</Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <Link to="/signup">Run a free audit</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
      <div className="font-mono text-3xl font-semibold tabular-nums">{value}</div>
      <div className="mt-1 text-sm text-slate-500">{label}</div>
    </div>
  );
}

function DimensionCard({ d }: { d: Dimension }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-xs font-semibold text-slate-400">{d.num}</span>
        <span className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          weight {d.weight}
        </span>
      </div>
      <h3 className="mt-2 text-base font-semibold">{d.title}</h3>
      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{d.body}</p>
    </div>
  );
}

function Step({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <li className="flex gap-4">
      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[var(--color-accent)] font-mono text-sm font-semibold text-white">
        {n}
      </span>
      <div>
        <div className="font-semibold">{title}</div>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{body}</p>
      </div>
    </li>
  );
}
